import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import {
  EnvironmentInjector,
  type EnvironmentProviders,
  inject,
  PLATFORM_ID,
  provideAppInitializer,
  runInInjectionContext,
} from '@angular/core';
import { type CanMatchFn, PRIMARY_OUTLET, Router } from '@angular/router';

import { firstValueFrom, isObservable } from 'rxjs';

import { type RouteGuardCheck, warmRoute } from './warm-route';

const INTENT_EVENTS = ['pointerover', 'focusin'] as const;

const isCanMatchFn = (guard: unknown): guard is CanMatchFn => typeof guard === 'function';

const guardCheck =
  (injector: EnvironmentInjector, router: Router): RouteGuardCheck =>
  async (route, segments) => {
    for (const guard of (route.canMatch ?? []).filter((candidate) => isCanMatchFn(candidate))) {
      const snapshot = router.routerState.snapshot.root;
      const result = runInInjectionContext(injector, () => guard(route, [...segments], snapshot));
      if ((isObservable(result) ? await firstValueFrom(result) : await result) !== true) return false;
    }
    return true;
  };

export const provideIntentPreloading = (): EnvironmentProviders =>
  provideAppInitializer(() => {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return;

    const document = inject(DOCUMENT);
    const router = inject(Router);
    const location = inject(Location);
    const canMatch = guardCheck(inject(EnvironmentInjector), router);
    const warmed = new Set<string>();

    const warm = (event: Event): void => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : undefined;
      if (!(link instanceof HTMLAnchorElement) || link.origin !== document.location.origin) return;

      const path = location.normalize(link.pathname);
      if (warmed.has(path)) return;

      warmed.add(path);
      const segments = router.parseUrl(path).root.children[PRIMARY_OUTLET]?.segments ?? [];
      warmRoute(router.config, segments, canMatch).catch(() => warmed.delete(path));
    };

    for (const type of INTENT_EVENTS) document.addEventListener(type, warm, { passive: true });
  });
